/**
 * Sanitizes HTML content by removing sensitive information and cleaning attributes
 */
import TurndownService from "turndown";

// Extend Window interface for custom properties
declare global {
    interface Window {
        workleapProductId?: string;
    }
}

// Digit-shaped PII; these also match digit runs inside hex IDs, so they never run on URLs
const PII_PATTERNS = [
    // Phone numbers (various formats)
    /(\+?1[-.\s]?)?\(?[0-9]{3}\)?[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}/g,
    // Credit card numbers (basic pattern)
    /\b(?:\d{4}[-\s]?){3}\d{4}\b/g,
    // Social Security Numbers
    /\b\d{3}-?\d{2}-?\d{4}\b/g,
    // IP addresses
    /\b(?:[0-9]{1,3}\.){3}[0-9]{1,3}\b/g
];

// Security Tokens and API Keys; safe to run on URLs because they key off a token shape or name
const SECRET_PATTERNS = [
    // JWT tokens (basic pattern)
    /eyJ[A-Za-z0-9_-]*\.[A-Za-z0-9_-]*\.[A-Za-z0-9_-]*/g,
    // Bearer tokens
    /Bearer\s+[A-Za-z0-9_-]+/gi,
    // API keys (various formats)
    /(?:api[_-]?key|apikey|access[_-]?token|auth[_-]?token|secret[_-]?key)[\s=:'"]*([A-Za-z0-9_-]{20,})/gi,
    // OAuth tokens
    /(?:oauth[_-]?token|access[_-]?token)[\s=:'"]*([A-Za-z0-9_-]{20,})/gi,
    // Session tokens
    /(?:session[_-]?token|session[_-]?id|csrf[_-]?token)[\s=:'"]*([A-Za-z0-9_-]{10,})/gi,
    // AWS keys
    /AKIA[0-9A-Z]{16}/g,
    // GitHub tokens
    /gh[pousr]_[A-Za-z0-9_]{36}/g,
    // Capability params in URLs; the suffix match also covers refresh_token, X-Amz-Security-Token and X-Amz/X-Goog-Signature
    /(?<![A-Za-z0-9])(?:token|sig|signature|password|client[_-]?secret)=[^&#\s"']+/gi
];

// Elements to remove completely (sensitive data + non-contextual elements)
const ELEMENTS_TO_REMOVE = [
    // Security-sensitive elements
    "input[type=\"password\"]",
    "input[type=\"hidden\"]",
    "script",
    "style",
    "noscript",
    "[data-sensitive]",
    "[data-private]",
    "[data-token]",
    "[data-key]",
    "[data-auth]",
    "[data-session]",
    "[data-csrf]",
    "[data-bearer]",
    "[data-jwt]",
    "[data-oauth]",
    ".sensitive",
    ".private",
    ".auth-token",
    ".api-key",
    ".session-token",
    ".csrf-token",
    ".bearer-token",
    ".jwt-token",
    ".oauth-token",
    "#auth-token",
    "#api-key",
    "#session-token",
    "#csrf-token",
    "#bearer-token",

    // Visual elements that don't provide context for LLM
    "svg",
    "canvas",
    "video",
    "audio",
    "iframe",
    "embed",
    "object",
    "picture",
    "source",
    "track",

    // Navigation and UI elements that are less relevant for content context
    "nav",
    ".navigation",
    ".nav",
    ".menu",
    ".sidebar",
    ".header",
    ".footer",
    ".breadcrumb",
    ".pagination",
    ".toolbar",
    ".controls",

    // Advertisement and tracking elements
    ".ad",
    ".ads",
    ".advertisement",
    ".banner",
    ".tracking",
    ".analytics",
    "[data-ad]",
    "[data-tracking]",

    // Social media widgets and embeds
    ".social-share",
    ".social-media",
    ".twitter-tweet",
    ".facebook-post",
    ".instagram-media",
    ".linkedin-post",

    // Application-specific elements
    "wl-ai-header-assistant"
];

// Attributes to remove from all elements
const ATTRIBUTES_TO_REMOVE = [
    "style",
    "class",
    "id",
    "data-token",
    "data-key",
    "data-secret",
    "data-auth",
    "data-session",
    "data-user-id",
    "data-api-key",
    "data-csrf",
    "data-bearer",
    "data-jwt",
    "data-oauth",
    "data-access-token",
    "data-refresh-token",
    "data-client-id",
    "data-client-secret",
    "authorization",
    "x-api-key",
    "x-auth-token",
    "x-csrf-token",
    "onclick",
    "onload",
    "onerror",
    "onmouseover",
    "onmouseout"
];

/**
 * Escapes characters that have special meaning inside XML/HTML attribute values
 */
function escapeAttr(value: string): string {
    return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * Removes PII patterns from text content
 */
function sanitizeTextContent(text: string): string {
    let sanitized = redactSecrets(text);

    PII_PATTERNS.forEach(pattern => {
        sanitized = sanitized.replace(pattern, "[REDACTED]");
    });

    return sanitized;
}

// tel:, sms:, mailto: etc. carry PII; http(s) and relative URLs carry resource IDs that must survive
function sanitizeUrlValue(value: string): string {
    return /^(?!https?:)[a-z][a-z0-9+.-]*:/i.test(value.trim()) ? sanitizeTextContent(value) : redactSecrets(value);
}

function redactSecrets(text: string): string {
    return SECRET_PATTERNS.reduce((sanitized, pattern) => sanitized.replace(pattern, "[REDACTED]"), text);
}

/**
 * Removes sensitive attributes from an element
 */
function cleanElementAttributes(element: Element): void {
    ATTRIBUTES_TO_REMOVE.forEach(attr => {
        if (element.hasAttribute(attr)) {
            element.removeAttribute(attr);
        }
    });

    // Remove all data-* attributes except safe ones and data-llm-* enrichment attributes
    Array.from(element.attributes).forEach(attr => {
        if (attr.name.startsWith("data-") &&
            !attr.name.match(/^data-(testid|cy|qa|llm-description|llm-editable)$/)) {
            element.removeAttribute(attr.name);
        }
    });

    // Sanitize URL attribute values to prevent secret leakage through Turndown output
    // (Turndown renders href/src as [text](href) and ![alt](src), surfacing their raw values).
    // http(s) and relative URLs only get secrets redacted: path IDs are not secrets, and redacting them breaks links the model builds.
    ["href", "src", "action"].forEach(attr => {
        if (element.hasAttribute(attr)) {
            element.setAttribute(attr, sanitizeUrlValue(element.getAttribute(attr) || ""));
        }
    });

    // Clear form values
    if (element instanceof HTMLInputElement) {
        element.value = "";
        element.removeAttribute("value");
    } else if (element instanceof HTMLTextAreaElement) {
        element.value = "";
        element.textContent = "";
    } else if (element instanceof HTMLSelectElement) {
        element.selectedIndex = -1;
    }
}

/**
 * Recursively sanitizes text nodes in the DOM tree
 */
function sanitizeTextNodes(node: Node): void {
    if (node.nodeType === Node.TEXT_NODE) {
        if (node.textContent) {
            node.textContent = sanitizeTextContent(node.textContent);
        }
    } else {
        node.childNodes.forEach(child => sanitizeTextNodes(child));
    }
}

/**
 * Removes the specific React template comment safely
 */
function removeReactTemplateComment(node: Node): void {
    const childNodes = Array.from(node.childNodes);

    childNodes.forEach(child => {
        if (child.nodeType === Node.COMMENT_NODE) {
            const commentText = child.textContent || "";

            // Multiple specific checks to ensure it's the exact React template comment
            const isReactTemplate =
                commentText.includes("This HTML file is a template.") &&
                commentText.includes("If you open it directly in the browser, you will see an empty page.") &&
                commentText.includes("The build step will place the bundled scripts") &&
                commentText.includes("pnpm start") &&
                (commentText.includes("pnpm build") || commentText.includes("pnpm run build"));

            if (isReactTemplate) {
                child.remove();
            }
        } else {
            removeReactTemplateComment(child);
        }
    });
}

/**
 * Optimizes content by keeping only the most relevant elements for LLM context
 */
function optimizeForLLMContext(clone: HTMLElement, options?: { dataLlmEnrichment?: boolean }): void {
    // Remove empty elements that don't provide context
    clone.querySelectorAll("*").forEach(element => {
        const textContent = element.textContent?.trim();
        const hasRelevantAttributes = element.hasAttribute("title") ||
                                     element.hasAttribute("alt") ||
                                     element.hasAttribute("aria-label") ||
                                     (!!options?.dataLlmEnrichment && (
                                         element.hasAttribute("data-llm-description") ||
                                         element.hasAttribute("data-llm-editable")
                                     ));

        // Remove elements that are empty and don't have relevant attributes
        if (!textContent && !hasRelevantAttributes && element.children.length === 0) {
            // Keep structural elements that might be important
            const structuralElements = ["main", "article", "section", "div", "header", "footer"];
            if (!structuralElements.includes(element.tagName.toLowerCase())) {
                element.remove();
            }
        }
    });

    // Simplify repetitive elements (like multiple similar list items)
    clone.querySelectorAll("ul, ol").forEach(list => {
        const items = list.querySelectorAll("li");
        if (items.length > 10) {
            // Keep first 5 and last 2 items, add a placeholder for the rest
            const itemsToKeep = Array.from(items).slice(0, 5).concat(Array.from(items).slice(-2));
            const placeholder = document.createElement("li");
            placeholder.textContent = `... [${items.length - 7} more items] ...`;

            // Remove all items
            items.forEach(item => item.remove());

            // Add back the items we want to keep and the placeholder
            itemsToKeep.slice(0, 5).forEach(item => list.appendChild(item));
            list.appendChild(placeholder);
            itemsToKeep.slice(5).forEach(item => list.appendChild(item));
        }
    });
}

/**
 * Checks if the current hostname is a Workleap domain
 * @returns true if it's a Workleap domain, false otherwise
 */
function isWorkleapDomain(): boolean {
    if (typeof window === "undefined") {
        return false;
    }

    const hostname = window.location.hostname;
    const parts = hostname.split(".");

    if (parts.length >= 2) {
        const mainDomain = parts.slice(-2).join("."); // Get last 2 parts (e.g., "workleap.com")

        return mainDomain === "workleap.com" ||
               mainDomain === "workleap-dev.com" ||
               mainDomain === "workleap-stg.com";
    }

    return false;
}

/**
 * Extracts the product identifier from the current subdomain
 * @returns The product name if found in subdomain, otherwise 'unknown'
 */
function getProductFromSubdomain(): string {
    if (typeof window === "undefined") {
        return "unknown";
    }

    const hostname = window.location.hostname;
    const subdomains = hostname.split(".");

    // Check if the main domain is a workleap domain
    if (!isWorkleapDomain()) {
        return "unknown";
    }

    // List of known product subdomains
    const knownProducts = ["officevibe", "pingboard", "onboarding", "management", "lms", "performance", "skills"];

    // Check if any subdomain matches our known products
    for (const subdomain of subdomains) {
        if (knownProducts.includes(subdomain.toLowerCase())) {
            return subdomain.toLowerCase();
        }
    }

    return "workleap";
}

/**
 * Checks if the current subdomain is an AI subdomain that should exclude page context
 * @returns true if it's an AI subdomain on a Workleap domain, false otherwise
 */
export function isAiSubdomain(): boolean {
    if (typeof window === "undefined") {
        return false;
    }

    const hostname = window.location.hostname;
    const parts = hostname.split(".");

    // Check if it's one of the specified Workleap domains
    if (!isWorkleapDomain()) {
        return false;
    }

    // For Workleap domains, the subdomain is the first part
    // Examples: ai.workleap.com, local-ai.workleap-dev.com
    const subdomain = parts[0];

    // Check if the actual subdomain is "ai" or "local-ai"
    return subdomain === "ai" || subdomain === "local-ai";
}

/**
 * Sanitizes the page body and converts it to Markdown for safe transmission as LLM context
 * @param maxSize Maximum size in characters (default: 16KB)
 * @returns Sanitized Markdown string or undefined if document is not available
 */
export function sanitizePageContext(maxSize: number = 16384, options?: { dataLlmEnrichment?: boolean }): string | undefined {
    if (typeof document === "undefined" || !document.body) {
        return undefined;
    }

    try {
        // Clone the body to avoid modifying the original DOM
        const clone = document.body.cloneNode(true) as HTMLElement;

        // Remove non-contextual and sensitive elements completely
        ELEMENTS_TO_REMOVE.forEach(selector => {
            clone.querySelectorAll(selector).forEach(element => {
                element.remove();
            });
        });

        // Clean attributes from all remaining elements
        clone.querySelectorAll("*").forEach(element => {
            cleanElementAttributes(element);
        });

        // Sanitize text content to remove PII patterns
        sanitizeTextNodes(clone);

        // Remove specific React template comment safely
        removeReactTemplateComment(clone);

        // Optimize content for LLM context
        optimizeForLLMContext(clone, options);

        if (options?.dataLlmEnrichment) {
            // Turndown marks elements whose text content is entirely whitespace as `isBlank`
            // and skips custom rules for them. Seed empty annotated elements with a zero-width
            // space so the llm-context rule below fires; the replacement strips it back out.
            clone.querySelectorAll("[data-llm-description], [data-llm-editable]").forEach(el => {
                if (!el.textContent?.trim()) {
                    el.textContent = "\u200B";
                }
            });
        }

        // Convert sanitized DOM to Markdown
        const turndown = new TurndownService({ headingStyle: "atx", codeBlockStyle: "fenced", bulletListMarker: "-" });

        if (options?.dataLlmEnrichment) {
            // Wrap elements annotated with data-llm-* in <page-section> tags so the model
            // receives structured semantic context alongside the node's text content.
            turndown.addRule("llm-context", {
                filter: (node: Node) =>
                    node instanceof HTMLElement && (node.hasAttribute("data-llm-description") || node.hasAttribute("data-llm-editable")),
                replacement: (content: string, node: Node) => {
                    const el = node as HTMLElement;
                    const parts: string[] = [];
                    const description = el.getAttribute("data-llm-description");
                    const editable = el.getAttribute("data-llm-editable");
                    if (description) { parts.push(`description="${escapeAttr(description)}"`); }
                    if (editable) { parts.push(`editable="${escapeAttr(editable)}"`); }
                    const attrStr = parts.length > 0 ? ` ${parts.join(" ")}` : "";
                    // Strip the zero-width space placeholder we may have added for empty elements
                    const trimmedContent = content.replace(/\u200B/g, "").trim();

                    return `\n\n<page-section${attrStr}>\n\n${trimmedContent}\n\n</page-section>\n\n`;
                }
            });
        }

        let markdown = turndown.turndown(clone.outerHTML);

        // Apply size limit to Markdown output
        if (markdown.length > maxSize) {
            markdown = markdown.substring(0, maxSize) + "<!-- [TRUNCATED] -->";
        }

        markdown = `The user is currently on the ${getProductFromSubdomain()} page ${window.location.href}\n\n${markdown}`;

        return markdown;
    } catch (error) {
        console.warn("Failed to sanitize page context:", error);

        return undefined;
    }
}
